import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import { objectToQueryParams } from "../../util/logic.ts";

export type RequisitionStatus = "PENDING" | "WAITING" | "IN_PROGRESS" | "COMPLETED";

export const REQUISITION_STATUSES: RequisitionStatus[] = ["PENDING", "WAITING", "IN_PROGRESS", "COMPLETED"];

export type RequisitionAttachment = {
    id: number;
    originalFilename: string;
    fileSizeBytes: number;
    mimeType: string;
    downloadUrl?: string;
};

export type AssignmentHistory = {
    id: string;
    createdAt: string;
    explanation: string | null;
    requisitionId: string;
    createdById: number | null;
    assignedToId: number;
    assignedTo: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        iconBackgroundColorClass?: string;
    } | null;
    createdBy: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        iconBackgroundColorClass?: string;
    } | null;
};

export type RequisitionMessage = {
    id: string;
    createdAt: string;
    body: string;
    fromName?: string;
    fromEmail?: string;
    isOutbound: boolean;
};

export type Requisition = {
    id: string;
    createdAt: string;
    updatedAt: string;
    status: RequisitionStatus;
    requesterEmail: string;
    requesterName: string | null;
    subject: string | null;
    body: string;
    assigneeId: number | null;
    assignee: {
        id: number;
        first_name: string;
        last_name: string;
        iconBackgroundColorClass?: string;
    } | null;
    attachmentCount: number;
    messageCount: number;
    rfq: {
        id: number;
        uniquePublicId: string;
        createdAt?: string;
    } | null;
    attachments?: RequisitionAttachment[];
    assignmentHistory?: AssignmentHistory[];
    messages?: RequisitionMessage[];
};

export type RequisitionListResponse = {
    results: Requisition[];
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export const getRequisitions = async (filters?: {
    status?: RequisitionStatus;
    search?: string;
    page?: number;
    limit?: number;
}): Promise<RequisitionListResponse> => {
    const queryParams = filters ? objectToQueryParams(filters) : null;
    const queryString = queryParams ? "?" + queryParams.toString() : "";
    const url = PURCHASER_API_BASE_URL + "/v2/requisitions" + queryString;

    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) {
        throw new Error("Failed to fetch requisitions");
    }
    return (await res.json()) as RequisitionListResponse;
};

export const getRequisition = async (id: string | number): Promise<Requisition> => {
    const url = `${PURCHASER_API_BASE_URL}/v2/requisitions/${id}`;
    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) {
        throw new Error("Failed to fetch requisition");
    }
    return (await res.json()) as Requisition;
};

export const claimRequisition = async (id: string | number): Promise<Requisition> => {
    const url = `${PURCHASER_API_BASE_URL}/v2/requisitions/${id}/claim`;
    const res = await fetch(url, { method: "POST", headers: getHeaders(), body: JSON.stringify({}) });
    if (!res.ok) {
        throw new Error("Failed to claim requisition");
    }
    return (await res.json()) as Requisition;
};

export const assignRequisition = async (id: string | number, payload: { userId: number | null; explanation: string }): Promise<Requisition> => {
    const url = `${PURCHASER_API_BASE_URL}/v2/requisitions/${id}/assign`;
    const res = await fetch(url, { method: "POST", headers: getHeaders(), body: JSON.stringify(payload) });
    if (!res.ok) {
        throw new Error("Failed to assign requisition");
    }
    return (await res.json()) as Requisition;
};

export const replyToRequisition = async (id: string | number, payload: { body: string; senderId: number }): Promise<void> => {
    const url = `${PURCHASER_API_BASE_URL}/v2/requisitions/${id}/reply`;
    const res = await fetch(url, { method: "POST", headers: getHeaders(), body: JSON.stringify(payload) });
    if (!res.ok) {
        throw new Error("Failed to reply to requisition");
    }
};

export const updateRequisition = async (id: string | number, payload: { subject?: string }): Promise<Requisition> => {
    const url = `${PURCHASER_API_BASE_URL}/v2/requisitions/${id}`;
    const res = await fetch(url, { method: "PATCH", headers: getHeaders(), body: JSON.stringify(payload) });
    if (!res.ok) {
        throw new Error("Failed to update requisition");
    }
    return (await res.json()) as Requisition;
};

export const convertToRFQ = async (id: string | number): Promise<{ uniquePublicId: string }> => {
    const url = `${PURCHASER_API_BASE_URL}/v2/requisitions/${id}/convert`;
    const res = await fetch(url, { method: "POST", headers: getHeaders(), body: JSON.stringify({}) });
    if (!res.ok) {
        throw new Error("Failed to convert requisition to RFQ");
    }
    return (await res.json()) as { uniquePublicId: string };
};

export default {
    getRequisitions,
    getRequisition,
    claimRequisition,
    assignRequisition,
    replyToRequisition,
    updateRequisition,
    convertToRFQ,
};
